import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_PixelateImage = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/pixelate-image',
			name: 'Pixelate Image Online — AI Pixelation Tool',
			description:
				'Pixelate part of an image with precision. Hide sensitive details, isolate subjects, or create selective visual effects. Try Lift.',
			url: 'https://liftapp.ai/pixelate-image',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/pixelate-image#product' },
				{ '@id': 'https://liftapp.ai/pixelate-image#howto' },
				{ '@id': 'https://liftapp.ai/pixelate-image#benefits' },
				{ '@id': 'https://liftapp.ai/pixelate-image#faq' },
				{ '@id': 'https://liftapp.ai/pixelate-image#why-lift' },
				{ '@id': 'https://liftapp.ai/pixelate-image#explore-tools' },
				{ '@id': 'https://liftapp.ai/pixelate-image#all-in-one' },
			],
		},
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/pixelate-image#product',
			name: 'Pixelate Image Online',
			description:
				'Pixelate part of an image with precision. Hide sensitive details, isolate subjects, or create selective visual effects.',
			url: 'https://liftapp.ai/pixelate-image',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: 4.8,
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/pixelate-image#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload Your Image',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Select the Area to Pixelate',
					text: 'Choose a face, background, specific object, or use the manual brush to define a custom region',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Adjust the Pixelation Intensity',
					text: 'Control how strong the effect appears. Increase intensity to fully conceal details or reduce it for a subtle treatment',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/pixelate-image#benefits',
			name: "Who can benefit from Lift's Image Pixelation Tool?",
			description:
				"Lift's image pixelation tool is ideal for content creators, marketers, small business owners, privacy-conscious users, and anyone who needs to pixelate part of a photo online.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Content creators — Control visual hierarchy by pixelating distracting backgrounds or secondary objects, keeping the main subject front and center.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Marketers & SMM managers — Remove off-brand or sensitive elements from product visuals before publishing to ads or social feeds.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Small business owners — Produce clean, professional images for listings and promotions without outsourcing to a designer.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Privacy-Conscious Users — Pixelate license plates, home addresses, faces, or other identifiable details before sharing photos online.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Casual Users — Fix unclear or unfocused image areas without prior editing experience or complex software.',
				},
			],
		},
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/pixelate-image#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift Image Pixelation Tool?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Lift's Image Pixelation Tool lets you pixelate a full photo or a specific area — such as a face, background, or object — using AI-assisted selection or a manual brush. It covers privacy protection, content preparation, and visual composition across personal and professional workflows.",
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift Image Pixelation work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Upload your photo, then select the target area — using automatic face, background, or object detection, or by drawing a custom region with the manual brush. Adjust the pixel block size using the slider, preview the result in real time, and download the finished image.',
					},
				},
				{
					'@type': 'Question',
					name: 'Will it keep hair, edges, and fine details?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift Image Pixelation Tool uses advanced edge detection to preserve delicate elements like hair, fur, shadows, and soft textures — so you get clean, natural-looking results without manual touch-ups.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, ads, or e-commerce.",
					},
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/pixelate-image#why-lift',
			name: "Why Lift's image pixelation tool stands out",
			description:
				"Lift's image pixelation tool combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
		},
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/pixelate-image#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},
		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/pixelate-image#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in/?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in/?auto_login=true`,
				},
			],
		},
		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Pixelate Image Online',
					item: 'https://liftapp.ai/pixelate-image',
				},
			],
		},
	],
};
